import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckIcon } from "lucide-react";
import { z } from "zod";
import { readClientsClientsGet, updateUserUsersUserIdPatch, UserPublicWithClientLinks } from "@/client-admin";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "@/components/ui/use-toast";
import { cn, PERMISSIONS, permissionSchema } from "@/lib/utils";
import { PricePreferenceAccordion } from "./PricePreference/PicePreferenceAccordion";
import { estimatedPricePreferenceDefaultvalues, estimatedPriceUpdatePreferencesSchema } from "./PricePreference/schema";

const userClientLinkSchema = z.object({
  client_id: z.number({ error: "Please select a client" }).min(1, "Please select a client"),
  permissions: z.array(permissionSchema).min(1, "At least one permission is required"),
  estimated_price_update_preferences: estimatedPriceUpdatePreferencesSchema,
  cc_recipients: z.array(z.string().email()),
  bcc_recipients: z.array(z.string().email()),
});

const editUserSchema = z.object({
  email: z.string().email(),
  first_name: z
    .string()
    .min(3, "First name must be at least 3 characters")
    .max(255, "First name must be less than 255 characters")
    .nullable(),
  last_name: z
    .string()
    .min(3, "Last name must be at least 3 characters")
    .max(255, "Last name must be less than 255 characters")
    .nullable(),
  isActive: z.boolean(),
  userClientLinks: z.array(userClientLinkSchema).min(1, "At least one client link is required"),
});

type EditUserFormValues = z.infer<typeof editUserSchema>;

type EditUserDialogProps = Pick<
  UserPublicWithClientLinks,
  "email" | "first_name" | "last_name" | "is_active" | "client_links" | "id"
>;

export function EditUserDialog({
  email,
  first_name: firstName,
  last_name: lastName,
  is_active: isActive,
  client_links,
  id,
}: EditUserDialogProps) {
  const [openPickers, setOpenPickers] = useState<{ [key: number]: boolean }>({});
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const form = useForm<z.input<typeof editUserSchema>, unknown, EditUserFormValues>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      email,
      first_name: firstName,
      last_name: lastName,
      isActive,
      userClientLinks: client_links.map((link) => ({
        client_id: link.client_id as number,
        permissions: link.permissions as z.infer<typeof permissionSchema>[],
        cc_recipients: link.cc_recipients || [],
        bcc_recipients: link.bcc_recipients || [],
        estimated_price_update_preferences: {
          emissions: {
            ...estimatedPricePreferenceDefaultvalues.emissions,
            ...link.estimated_price_update_preferences?.emissions,
          },
          gas: {
            ...estimatedPricePreferenceDefaultvalues.gas,
            ...link.estimated_price_update_preferences?.gas,
          },
          electricity: {
            ...estimatedPricePreferenceDefaultvalues.electricity,
            ...link.estimated_price_update_preferences?.electricity,
          },
          sparkspread: {
            ...estimatedPricePreferenceDefaultvalues.sparkspread,
            ...link.estimated_price_update_preferences?.sparkspread,
          },
        },
      })),
    },
    mode: "onChange",
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "userClientLinks",
  });

  const { data: clientsData } = useQuery({
    queryKey: ["clients"],
    queryFn: () => readClientsClientsGet(),
  });

  const clientOptions =
    clientsData?.data.filter(
      (client) => !form.getValues("userClientLinks").some((link) => link.client_id === client.id),
    ) || [];

  const updateUserMutation = useMutation({
    mutationFn: async (data: EditUserFormValues) => {
      const response = await updateUserUsersUserIdPatch({
        userId: id,
        requestBody: {
          user_in: {
            email: data.email,
            first_name: data.first_name ?? null,
            last_name: data.last_name ?? null,
            is_active: data.isActive,
          },
          client_links: data.userClientLinks.map((link) => ({
            client_id: link.client_id,
            permissions: link.permissions || [],
            cc_recipients: [...new Set(link.cc_recipients)],
            bcc_recipients: [...new Set(link.bcc_recipients)],
            estimated_price_update_preferences: {
              emissions: link.estimated_price_update_preferences.emissions,
              gas: link.estimated_price_update_preferences.gas,
              electricity: link.estimated_price_update_preferences.electricity,
              sparkspread: link.estimated_price_update_preferences.sparkspread,
            },
          })),
        },
      });

      return response;
    },
    onSuccess: () => {
      form.reset();
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast({
        title: "Success",
        description: "User updated successfully",
      });
      setOpen(false);
    },
    onError: (error) => {
      console.error(error);
      let errorMessage = "Failed to update user. Please try again later.";
      if (error instanceof Error && error.message) {
        errorMessage = error.message;
      }
      form.setError("root", {
        type: "manual",
        message: errorMessage,
      });
    },
  });

  async function onSubmit(data: EditUserFormValues) {
    try {
      if (hasFormErrors) {
        console.error("Form has validation errors:", formErrors);
        return;
      }

      await updateUserMutation.mutateAsync(data);
    } catch (error) {
      console.error("Error in form submission:", error);
      throw error; // Re-throw to let the mutation error handler deal with it
    }
  }

  const formErrors = form.formState.errors;
  const hasFormErrors = Object.keys(formErrors).length > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        form.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>Edit</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] md:max-w-[800px] lg:max-w-[1200px]">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>Edit the user details and assign their permissions.</DialogDescription>
        </DialogHeader>
        <Form {...form} key={id}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            {/* Email Field */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" disabled />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* First Name Field */}
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Last Name Field */}
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Active Checkbox */}
            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                      }}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>Is this user account active?</FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {/* Client Links */}
            <div className="space-y-4">
              {fields.map((field, index) => (
                <div key={`remove-field-${field.id}-${index}`} className="space-y-4 rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">Client Link {index + 1}</h4>
                    {fields.length > 1 && (
                      <Button type="button" variant="destructive" size="sm" onClick={() => remove(index)}>
                        Remove
                      </Button>
                    )}
                  </div>

                  {/* Client Picker */}
                  <FormField
                    control={form.control}
                    name={`userClientLinks.${index}.client_id`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Client</FormLabel>
                        <FormControl>
                          <Popover
                            open={openPickers[index]}
                            onOpenChange={(open) => setOpenPickers({ ...openPickers, [index]: open })}
                            modal
                          >
                            <PopoverTrigger asChild>
                              <Button variant="outline" role="combobox" className="w-full justify-between">
                                {clientsData?.data?.find((client) => client.id === field.value)?.client_name ||
                                  "Select client..."}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-full p-0">
                              <Command>
                                <CommandInput placeholder="Search client..." className="h-9" />
                                <CommandEmpty>No client found.</CommandEmpty>
                                <CommandGroup>
                                  <CommandList>
                                    {clientOptions.map((client) => (
                                      <CommandItem
                                        key={client.id}
                                        value={client.client_name}
                                        data-value={client.client_code}
                                        onSelect={() => {
                                          field.onChange(client.id);
                                          setOpenPickers({ ...openPickers, [index]: false });
                                        }}
                                      >
                                        <CheckIcon
                                          className={cn(
                                            "mr-2 h-4 w-4",
                                            field.value === client.id ? "opacity-100" : "opacity-0",
                                          )}
                                        />
                                        {client.client_name}
                                      </CommandItem>
                                    ))}
                                  </CommandList>
                                </CommandGroup>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Permissions */}
                  <FormField
                    control={form.control}
                    name={`userClientLinks.${index}.permissions`}
                    render={({ field }) => (
                      <FormItem>
                        <div className="mb-4">
                          <FormLabel className="flex items-center justify-between">
                            Permissions
                            {form.formState.errors.userClientLinks?.[index]?.permissions && (
                              <span className="text-sm font-normal text-destructive">
                                {form.formState.errors.userClientLinks?.[index]?.permissions?.message}
                              </span>
                            )}
                          </FormLabel>
                          <FormDescription>Select the permissions for this client link.</FormDescription>
                        </div>
                        <div
                          className={cn(
                            "grid grid-cols-2 gap-4",
                            form.formState.errors.userClientLinks?.[index]?.permissions &&
                              "rounded-md border border-destructive/50 p-4",
                          )}
                        >
                          {PERMISSIONS.map((permission) => (
                            <FormItem key={permission.id} className="flex flex-row items-start space-x-3 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(permission.id)}
                                  onCheckedChange={(checked) => {
                                    const currentPermissions = field.value || [];
                                    return checked
                                      ? field.onChange([...currentPermissions, permission.id])
                                      : field.onChange(currentPermissions?.filter((value) => value !== permission.id));
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="font-normal">{permission.label}</FormLabel>
                            </FormItem>
                          ))}
                        </div>
                      </FormItem>
                    )}
                  />
                  <PricePreferenceAccordion index={index} />
                </div>
              ))}

              {/* Add Client Link Button */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() =>
                  append({
                    // @ts-expect-error allow empty client_id for new client link default value
                    client_id: undefined,
                    permissions: PERMISSIONS.map((permission) => permission.id),
                    cc_recipients: [],
                    bcc_recipients: [],
                    estimated_price_update_preferences: {
                      emissions: { ...estimatedPricePreferenceDefaultvalues.emissions },
                      gas: { ...estimatedPricePreferenceDefaultvalues.gas },
                      electricity: { ...estimatedPricePreferenceDefaultvalues.electricity },
                      sparkspread: { ...estimatedPricePreferenceDefaultvalues.sparkspread },
                    },
                  })
                }
              >
                Add Another Client Link
              </Button>
            </div>

            {/* Form Footer */}
            <DialogFooter className="flex flex-col gap-4">
              {hasFormErrors && (
                <div className="w-full">
                  <div className="space-y-2 rounded-md bg-destructive/15 p-3">
                    <div className="text-sm font-medium text-destructive">
                      Please fix the following errors before submitting:
                    </div>
                    <ul className="list-inside list-disc space-y-1 text-sm text-destructive/90">
                      {formErrors?.email && <li>Email: {formErrors?.email?.message}</li>}
                      {formErrors?.first_name && <li>First Name: {formErrors?.first_name?.message}</li>}
                      {formErrors?.last_name && <li>Last Name: {formErrors?.last_name?.message}</li>}
                      {formErrors?.isActive && <li>Active Status: {formErrors?.isActive?.message}</li>}
                      {formErrors?.userClientLinks && (
                        <>
                          {Array.isArray(formErrors?.userClientLinks) &&
                            formErrors?.userClientLinks.map(
                              (linkError, index) =>
                                linkError && (
                                  <li key={index}>
                                    Client Link {index + 1}:
                                    <ul className="list-circle ml-4 list-inside">
                                      {linkError?.client_id && <li>{linkError?.client_id?.message}</li>}
                                      {linkError?.permissions && <li>{linkError?.permissions?.message}</li>}
                                      {linkError?.cc_recipients && (
                                        <li>CC Recipients: {linkError?.cc_recipients?.message}</li>
                                      )}
                                      {linkError?.bcc_recipients && (
                                        <li>BCC Recipients: {linkError?.bcc_recipients?.message}</li>
                                      )}
                                    </ul>
                                  </li>
                                ),
                            )}
                          {!Array.isArray(formErrors?.userClientLinks) && formErrors?.userClientLinks?.message && (
                            <li>{formErrors?.userClientLinks?.message}</li>
                          )}
                        </>
                      )}
                      {formErrors?.root && <li>{formErrors?.root.message}</li>}
                    </ul>
                  </div>
                </div>
              )}

              <Button type="submit" disabled={updateUserMutation.isPending}>
                {updateUserMutation.isPending ? "Updating..." : "Submit"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
